import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { STATUS_PRESETS, readStatus, readPhone, readDesignation, parseMeta } from "./status";
import { fetchDirectoryScope, companyLabelFor } from "./directoryScope";
import { useClampToViewport } from "./Popover";
import Avatar from "./Avatar";

const userCache = new Map<string, CometChat.User>();

/** Wraps an avatar: hover shows a profile card, click opens the full
 * profile. Safe to nest inside a parent <button> (a row's select action) —
 * click stopPropagation keeps the two from firing together. */
export default function AvatarHoverCard({
  uid,
  name,
  onOpenProfile,
  children,
}: {
  uid: string;
  name: string;
  onOpenProfile?: (uid: string) => void;
  children: ReactNode;
}) {
  const [show, setShow] = useState(false);
  // The whole user, not just their status — the card also shows email,
  // designation and (when they've opted in) phone, all of which live in
  // the same metadata blob, so one fetch covers the lot.
  const [user, setUser] = useState<CometChat.User | null | undefined>(undefined);
  // Company and email come from the directory scope rather than the
  // user's own metadata: company belongs to the email domain, and the
  // scope's email is from Supabase Auth, which is populated even for
  // people who haven't signed in yet. The call is cached, so hovering
  // costs nothing.
  const [org, setOrg] = useState<{ company: string | null; email: string } | null>(null);
  const timer = useRef<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // Same viewport-edge overflow as the emoji/reminder popovers — the card
  // centers itself on the avatar via CSS, which breaks down for an avatar
  // sitting close to the right edge (every "mine" message): the card was
  // overlapping whatever bubble happened to be nearby instead of floating
  // cleanly below its own avatar, exactly what Slack/Teams avoid by
  // keeping these cards inside the viewport regardless of trigger position.
  useClampToViewport(cardRef, [show, user, org]);

  const load = () => {
    fetchDirectoryScope()
      .then((s) => {
        const entry = s.people[uid];
        setOrg({ company: companyLabelFor(entry), email: entry?.email || "" });
      })
      .catch(() => setOrg(null));

    const cached = userCache.get(uid);
    if (cached) {
      setUser(cached);
      return;
    }
    CometChat.getUser(uid)
      .then((u) => {
        userCache.set(uid, u);
        setUser(u);
      })
      .catch(() => setUser(null));
  };

  const enter = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      load();
      setShow(true);
    }, 380);
  };
  const leave = () => {
    if (timer.current) window.clearTimeout(timer.current);
    setShow(false);
  };

  const status = readStatus(user);
  const designation = readDesignation(user);
  // Scope email first (Supabase Auth), metadata second as a fallback for
  // anyone the scope doesn't cover.
  const email = org?.email || (parseMeta(user).dbmEmail as string | undefined) || "";
  // Honours the profile's "Visible to others" toggle — a hover card must
  // not become a way around a privacy setting the person deliberately set.
  const { phone, visible: phoneVisible } = readPhone(user);
  const showPhone = !!phone && phoneVisible;

  return (
    <span
      className="avatar-hover"
      onMouseEnter={enter}
      onMouseLeave={leave}
      onClick={(e) => {
        e.stopPropagation();
        setShow(false);
        onOpenProfile?.(uid);
      }}
    >
      {children}
      {show && (
        <div className="avatar-hover__card" ref={cardRef}>
          <div className="avatar-hover__head">
            <Avatar uid={uid} name={name} size="av" />
            <div className="avatar-hover__id">
              <div className="avatar-hover__name">{name}</div>
              {designation && <div className="avatar-hover__role">{designation}</div>}
              {org?.company && <div className="avatar-hover__co">{org.company}</div>}
            </div>
          </div>

          {status && (
            <div className="avatar-hover__status">
              <span
                className="status-dot"
                style={{ background: STATUS_PRESETS[status.preset].color }}
              />
              {STATUS_PRESETS[status.preset].label}
              {status.reason && <span className="avatar-hover__reason"> · {status.reason}</span>}
            </div>
          )}

          {(email || showPhone) && (
            <div className="avatar-hover__contact">
              {email && (
                <div className="avatar-hover__line" title={email}>
                  <span className="avatar-hover__ico">@</span>
                  {email}
                </div>
              )}
              {showPhone && (
                <div className="avatar-hover__line" title={phone}>
                  <span className="avatar-hover__ico">☏</span>
                  {phone}
                </div>
              )}
            </div>
          )}

          {onOpenProfile && (
            <div className="avatar-hover__foot">View profile</div>
          )}
        </div>
      )}
    </span>
  );
}
